//region header
//--------------------------------------------------------------------------------------------------
//
//  Description: Move BLPU seed point dialog
//
//  Copyright:    © 2023 - 2026 Idox Software Limited.
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//region Version 1.0.0.0
//    001   06/11/23 Sean Flook        IMANN-175 Initial Revision.
//    002   24.11.23 Sean Flook                  Moved Box and Stack to @mui/system, renamed successor to successorCrossRef and simplified handleFinaliseClose.
//    003   02.01.24 Sean Flook                  Changed console.log to console.error for error messages.
//    004   16.01.24 Sean Flook                  Changes required to fix warnings.
//    005   25.01.24 Sean Flook                  Changes required after UX review.
//    006   20.02.24 Sean Flook             MUL6 Updated title.
//    007   27.02.24 Sean Flook            MUL15 Fixed dialog title styling.
//    008   11.03.24 Sean Flook            GLB12 Removed bottom margin.
//    009   12.03.24 Sean Flook             MUL7 Handle saving when X is clicked and user chooses to save changes.
//    010   12.03.24 Sean Flook             MUL8 Display an alert if the save fails.
//    011   04.04.24 Sean Flook                  Added parentUprn to mapContext search data for properties.
//    012   11.04.24 Sean Flook        IMANN-384 Check we have a parent UPRN before changing it to a string.
//    013   23.05.24 Sean Flook        IMANN-486 Changed seqNo to seqNum.
//    014   19.06.24 Sean Flook        IMANN-629 Changes to code so that current user is remembered and a 401 error displays the login dialog.
//    015   09.07.24 Sean Flook        IMANN-731 Corrected Scottish data.
//    016   10.09.24 Sean Flook        IMANN-980 Only write to the console if the user has the showMessages right.
//endregion Version 1.0.0.0
//region Version 1.0.1.0
//    017   14.10.24 Sean Flook       IMANN-1016 Changes required to handle LLPG Streets.
//endregion Version 1.0.1.0
//region Version 1.0.2.0
//    018   18.11.24 Sean Flook       IMANN-1056 Use the new getPropertyListDetails method.
//    019   21.11.24 Sean Flook       IMANN-1065 Correctly handle when the data is updated.
//endregion Version 1.0.2.0
//region Version 1.0.5.0
//    020   27.01.25 Sean Flook       IMANN-1077 Upgraded MUI to v6.
//endregion Version 1.0.5.0
//region Version 1.0.6.0
//    021   22.07.25 Sean Flook       IMANN-1855 Use the new ADSCircularProgress component.
//endregion Version 1.0.6.0
//region Version 1.0.6.4
//    022   30.09.25 Sean Flook       IMANN-2074 Reset the variables when the dialog is closed.
//endregion Version 1.0.6.4
//region Version 1.0.7.0
//    023   13.06.25 Sean Flook       IMANN-1812 Moved controls from @mui/system to @mui/material.
//    024   28.08.25 Sean Flook       IMANN-1812 Changes required to update MUI to v7.
//    025   24.09.25 Sean Flook       IMANN-1031 Prevent errors from being reset when saving multiple records.
//    026   27.10.25 Sean Flook       IMANN-2092 Use the new spatialReferenceWkid property from mapContext to set the spatialReference used by the layers in the map.
//    027   06.11.25 Sean Flook       IMANN-2092 Correctly set the spatialReferenceWkid variable.
//    028   17.12.25 Sean Flook       IMANN-2196 Handle closing the selection control.
//    029   20.02.26 Sean Flook       IMANN-2329 Updated call to resetPropertyErrors.
//    030   06.03.26 Sean Flook       IMANN-2109 Renamed the map context property currentSearchData to currentMapSearchData to better reflect its purpose.
//endregion Version 1.0.7.0
//region Version 1.0.7.1
//    031   27.04.26 Sean Flook       IMANN-2411 Correctly compare UPRNs.
//endregion Version 1.0.7.1
//
//--------------------------------------------------------------------------------------------------
//endregion header

import React, { forwardRef, useContext, useState, useRef, Fragment, useEffect } from "react";
import PropTypes from "prop-types";

import UserContext from "../context/userContext";
import MapContext from "../context/mapContext";
import LookupContext from "../context/lookupContext";
import SettingsContext from "../context/settingsContext";
import PropertyContext from "../context/propertyContext";
import SelectionContext from "../context/selectionContext";

import { ArraysEqual } from "../utils/HelperUtils";
import {
  addressToTitleCase,
  GetPropertyMapData,
  SaveProperty,
  getClassificationCode,
  getPropertyListDetails,
} from "../utils/PropertyUtils";

import {
  Slide,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Button,
  Divider,
  Grid,
  Tooltip,
  Snackbar,
  Alert,
  Box,
  Stack,
} from "@mui/material";

import ADSMapDialog from "../components/ADSMapDialog";
import ADSWizardAddressList from "../components/ADSWizardAddressList";
import ADSCircularProgress from "../components/ADSCircularProgress";

import MessageDialog from "./MessageDialog";

import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ErrorIcon from "@mui/icons-material/Error";

import { adsBlueA, adsOffWhite } from "../utils/ADSColours";
import {
  blueButtonStyle,
  redButtonStyle,
  dialogTitleStyle,
  tooltipStyle,
  GetAlertStyle,
  GetAlertIcon,
  GetAlertSeverity,
} from "../utils/ADSStyles";
import { useTheme } from "@mui/material/styles";

MoveBLPUDialog.propTypes = {
  propertyUprns: PropTypes.array.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function MoveBLPUDialog({ propertyUprns, isOpen, onClose }) {
  const theme = useTheme();

  const userContext = useRef(useContext(UserContext));
  const mapContext = useContext(MapContext);
  const lookupContext = useContext(LookupContext);
  const settingsContext = useContext(SettingsContext);
  const propertyContext = useContext(PropertyContext);
  const selectionContext = useContext(SelectionContext);

  const [showDialog, setShowDialog] = useState(false);
  const [openMessageDialog, setOpenMessageDialog] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [finaliseErrors, setFinaliseErrors] = useState([]);
  const [haveErrors, setHaveErrors] = useState(false);
  const [viewErrors, setViewErrors] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);

  const [data, setData] = useState([]);
  const [currentUprns, setCurrentUprns] = useState([]);
  const [checked, setChecked] = useState([]);
  const checkedRef = useRef([]);

  const [rangeProcessedCount, setRangeProcessedCount] = useState(0);

  const spatialReferenceWkid = useRef(mapContext.spatialReference || 27700);
  const isOpenRef = useRef(false);
  const properties = useRef(null);
  const savedProperty = useRef(null);
  const updateErrors = useRef([]);
  const totalUpdateCount = useRef(0);
  const updatedCount = useRef(0);
  const failedCount = useRef(0);
  const failedIds = useRef([]);

  const startExtent = useRef(
    mapContext.currentExtent
      ? {
          xmin: mapContext.currentExtent.xmin,
          ymin: mapContext.currentExtent.ymin,
          xmax: mapContext.currentExtent.xmax,
          ymax: mapContext.currentExtent.ymax,
          zoom: mapContext.currentExtent.zoom,
          spatialReference: mapContext.currentExtent.spatialReference,
        }
      : null
  );

  /**
   * Event to handle when the dialog closes.
   *
   * @param {object} event The event object.
   * @param {string} reason The reason why the dialog is closing.
   */
  const handleDialogClose = (event, reason) => {
    event.stopPropagation();
    if (reason === "escapeKeyDown") handleCancelClick();
  };

  /**
   * Event to handle when the cancel button is clicked.
   */
  const handleCancelClick = () => {
    setOpenMessageDialog(true);
  };

  /**
   * Event to handle when the message dialog closes.
   *
   * @param {string} action The action to take after closing the dialog.
   */
  const handleMessageDialogClose = (action) => {
    setOpenMessageDialog(false);

    switch (action) {
      case "close":
        if (onClose) onClose(false);
        else setShowDialog(false);
        break;

      case "save":
        handleFinish();
        break;

      default:
        break;
    }
  };

  /**
   * Event to handle updating the list of checked records.
   *
   * @param {Array} updatedChecked The list of checked records.
   */
  const handleCheckedChanged = (updatedChecked) => {
    setChecked(updatedChecked);
    checkedRef.current = updatedChecked;
  };

  /**
   * Event to handle when the data has been changed.
   *
   * @param {object} updatedData The updated data.
   */
  const handleDataChanged = (updatedData) => {
    setData(updatedData);
    // if (onDataChange) onDataChange(updatedData);
  };

  /**
   * Event to handle when the list of errors changes.
   *
   * @param {Array} finaliseErrors The list of errors.
   */
  const handleErrorChanged = (finaliseErrors) => {
    setFinaliseErrors(finaliseErrors);
    // if (onErrorChanged) onErrorChanged(finaliseErrors);
  };

  /**
   * Event to handle when the finish button is clicked.
   */
  const handleFinish = async () => {
    setUpdating(true);
    properties.current = [];
    savedProperty.current = [];
    totalUpdateCount.current = propertyUprns.length;
    updatedCount.current = 0;
    failedCount.current = 0;
    failedIds.current = [];
    setHaveErrors(false);
    setViewErrors(false);
    updateErrors.current = [];
    setFinaliseErrors([]);
    propertyContext.resetPropertyErrors(propertyContext.creatingPropertyRange, true);

    for (const property of data) {
      const currentProperty = await GetPropertyMapData(property.uprn, userContext.current);

      if (currentProperty) {
        const engLpi = currentProperty.lpis.filter((x) => x.language === "ENG");
        if (engLpi && engLpi.length > 0) {
          properties.current.push({
            id: currentProperty.pkId,
            uprn: currentProperty.uprn,
            address: addressToTitleCase(engLpi[0].address, engLpi[0].postcode),
          });
        }

        // Check to see if something has changed
        if (
          currentProperty.xcoordinate !== property.easting ||
          currentProperty.ycoordinate !== property.northing ||
          currentProperty.rpc !== property.blpu.rpc ||
          !property.other.notes
        ) {
          const minPkIdNote =
            currentProperty.blpuNotes && currentProperty.blpuNotes.length > 0
              ? currentProperty.blpuNotes.reduce((prev, curr) => (prev.pkId < curr.pkId ? prev : curr))
              : null;
          let newPkId = !minPkIdNote || !minPkIdNote.pkId || minPkIdNote.pkId > -10 ? -10 : minPkIdNote.pkId - 1;
          const maxSeqNum =
            currentProperty.blpuNotes && currentProperty.blpuNotes.length > 0
              ? currentProperty.blpuNotes.reduce((prev, curr) => (prev.seqNum > curr.seqNum ? prev : curr))
              : null;
          let newSeqNum = maxSeqNum && maxSeqNum.seqNum ? maxSeqNum.seqNum + 1 : 1;

          const updatedNotes = currentProperty.blpuNotes ? [...currentProperty.blpuNotes] : [];
          if (property.other.note) {
            property.other.note.forEach((note) => {
              updatedNotes.push({
                uprn: property.uprn,
                note: note,
                changeType: "I",
                pkId: newPkId,
                seqNum: newSeqNum,
              });

              newPkId -= 1;
              newSeqNum += 1;
            });
          }

          const updatedProperty = settingsContext.isScottish
            ? {
                blpuStateDate: currentProperty.blpuStateDate,
                parentUprn: currentProperty.parentUprn,
                neverExport: currentProperty.neverExport,
                siteSurvey: currentProperty.siteSurvey,
                uprn: currentProperty.uprn,
                logicalStatus: currentProperty.logicalStatus,
                endDate: currentProperty.endDate,
                startDate: currentProperty.startDate,
                blpuState: currentProperty.blpuState,
                custodianCode: currentProperty.custodianCode,
                level: currentProperty.level,
                xcoordinate: property.easting,
                ycoordinate: property.northing,
                pkId: currentProperty.pkId,
                changeType: "U",
                rpc: property.blpu.rpc ? property.blpu.rpc : currentProperty.rpc,
                blpuAppCrossRefs: currentProperty.blpuAppCrossRefs,
                blpuProvenances: currentProperty.blpuProvenances,
                blpuNotes: updatedNotes,
                classifications: currentProperty.classifications,
                organisations: currentProperty.organisations,
                successorCrossRefs: currentProperty.successorCrossRefs,
                lpis: currentProperty.lpis,
              }
            : {
                changeType: "U",
                blpuStateDate: currentProperty.blpuStateDate,
                rpc: property.blpu.rpc ? property.blpu.rpc : currentProperty.rpc,
                startDate: currentProperty.startDate,
                endDate: currentProperty.endDate,
                parentUprn: currentProperty.parentUprn,
                neverExport: currentProperty.neverExport,
                siteSurvey: currentProperty.siteSurvey,
                uprn: currentProperty.uprn,
                logicalStatus: currentProperty.logicalStatus,
                blpuState: currentProperty.blpuState,
                blpuClass: currentProperty.blpuClass,
                localCustodianCode: currentProperty.localCustodianCode,
                organisation: currentProperty.organisation,
                xcoordinate: property.easting,
                ycoordinate: property.northing,
                wardCode: currentProperty.wardCode,
                parishCode: currentProperty.parishCode,
                pkId: currentProperty.pkId,
                blpuAppCrossRefs: currentProperty.blpuAppCrossRefs,
                blpuProvenances: currentProperty.blpuProvenances,
                blpuNotes: updatedNotes,
                lpis: currentProperty.lpis,
              };

          if (updatedProperty) {
            SaveProperty(
              updatedProperty,
              false,
              userContext.current,
              propertyContext,
              settingsContext.isScottish,
              false
            ).then((result) => {
              if (result) {
                updatedCount.current++;
                savedProperty.current.push(result);
                setRangeProcessedCount(updatedCount.current + failedCount.current);
              }
            });
          } else {
            failedCount.current++;
          }
        }
      }
    }
  };

  /**
   * Event to handle when the alert is closed.
   *
   * @param {object} event The event object
   * @param {string} reason The reason why the alert is closing.
   * @returns
   */
  const handleAlertClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setAlertOpen(false);
  };

  useEffect(() => {
    if (data && data.filter((x) => x.language === "ENG").length > 0) {
      let minX = 9999999.999;
      let minY = 9999999.999;
      let maxX = 0.0;
      let maxY = 0.0;

      data
        .filter((x) => x.language === "ENG")
        .forEach((x) => {
          if (x.easting < minX) minX = x.easting;
          if (x.northing < minY) minY = x.northing;
          if (x.easting > maxX) maxX = x.easting;
          if (x.northing > maxY) maxY = x.northing;
        });

      startExtent.current = {
        xmin: minX,
        ymin: minY,
        xmax: maxX,
        ymax: maxY,
        zoom: null,
        spatialReference: { wkid: spatialReferenceWkid.current },
      };
    }
  }, [data]);

  useEffect(() => {
    const getPropertyDetails = async () => {
      const newSearchData = await getPropertyListDetails(propertyUprns, userContext.current);

      let newPropertyData = [];
      if (newSearchData) {
        newPropertyData = newSearchData.map((x) => {
          const postTownRec = lookupContext.currentLookups.postTowns.find(
            (p) =>
              ((x.post_town && x.post_town.length > 0 && p.postTown.toUpperCase() === x.post_town.toUpperCase()) ||
                p.postTown.toUpperCase() === x.town.toUpperCase()) &&
              p.language.toUpperCase() === x.language.toUpperCase()
          );

          return {
            uprn: x.uprn,
            id: `${x.language}_${x.uprn}`,
            language: x.language,
            easting: x.easting,
            northing: x.northing,
            parentUprn: x.parent_uprn,
            blpu: {
              logicalStatus: x.logical_status,
              rpc: null,
              state: null,
              stateDate: null,
              classification: x.classification_code,
              startDate: null,
            },
            lpi: {
              logicalStatus: x.logical_status,
              level: null,
              officialAddress: null,
              postallyAddressable: null,
              startDate: null,
            },
            other: {
              provCode: null,
              provStartDate: null,
              notes: null,
            },
            addressDetails: {
              id: null,
              address: addressToTitleCase(x.formattedaddress, x.postcode),
              mapLabel: x.full_building_desc,
              saoStartNumber: null,
              saoStartSuffix: null,
              saoEndNumber: null,
              saoEndSuffix: null,
              saoText: null,
              paoStartNumber: null,
              paoStartSuffix: null,
              paoEndNumber: null,
              paoEndSuffix: null,
              paoText: null,
              paoDetails: null,
              usrn: x.usrn,
              postcodeRef: -1,
              postTownRef: postTownRec ? postTownRec.postTownRef : 0,
              included: true,
            },
          };
        });

        setData(newPropertyData);
        setCurrentUprns(propertyUprns);
      }

      return newPropertyData;
    };

    if (showDialog && propertyUprns && propertyUprns.length > 0 && !ArraysEqual(propertyUprns, currentUprns)) {
      getPropertyDetails();
    }
  }, [propertyUprns, currentUprns, lookupContext.currentLookups.postTowns, showDialog]);

  useEffect(() => {
    if (isOpenRef.current !== isOpen) {
      isOpenRef.current = isOpen;
      setShowDialog(isOpen);
    }

    if (!isOpen) {
      setHaveErrors(false);
      setCompleted(false);
      setUpdating(false);
      setFinaliseErrors([]);
      setViewErrors(false);
      setAlertOpen(false);
      setData([]);
      setCurrentUprns([]);
      setChecked([]);
      checkedRef.current = [];
      setRangeProcessedCount(0);
      isOpenRef.current = false;
      properties.current = null;
      savedProperty.current = null;
      updateErrors.current = [];
      totalUpdateCount.current = 0;
      updatedCount.current = 0;
      failedCount.current = 0;
      failedIds.current = [];
    }
  }, [isOpen]);

  useEffect(() => {
    if (mapContext.currentPinSelected) {
      if (!checkedRef.current.includes(mapContext.currentPinSelected)) {
        setChecked([mapContext.currentPinSelected]);
        checkedRef.current = [mapContext.currentPinSelected];
      }
    } else if (checkedRef.current && checkedRef.current.length > 0) {
      setChecked([]);
      checkedRef.current = [];
    }
  }, [mapContext.currentPinSelected]);

  useEffect(() => {
    if (mapContext.currentWizardPoint && data) {
      const property = data.find((x) => x.id === mapContext.currentWizardPoint.pointId);
      if (
        property &&
        (property.easting !== mapContext.currentWizardPoint.x || property.northing !== mapContext.currentWizardPoint.y)
      ) {
        const updatedData = data.map(
          (x) =>
            [
              {
                uprn: property.uprn,
                id: property.id,
                language: property.language,
                easting: mapContext.currentWizardPoint.x,
                northing: mapContext.currentWizardPoint.y,
                parentUprn: property.parentUprn,
                blpu: property.blpu,
                lpi: property.lpi,
                other: property.other,
                addressDetails: property.addressDetails,
              },
            ].find((rec) => rec.id === x.id) || x
        );

        setData(updatedData);
      }
    }
  }, [mapContext.currentWizardPoint, data]);

  useEffect(() => {
    const getErrorList = (currentErrors) => {
      const errorList = [];

      if (currentErrors.blpu && currentErrors.blpu.length > 0) {
        for (const error of currentErrors.blpu) {
          const errorStr = `BLPU [${error.field}]: ${[...new Set(error.errors)].join(", ")}`;
          if (!errorList.includes(errorStr)) errorList.push(errorStr);
        }
      }

      if (currentErrors.lpi && currentErrors.lpi.length > 0) {
        for (const error of currentErrors.lpi) {
          const errorStr = `LPI [${error.field}]: ${[...new Set(error.errors)].join(", ")}`;
          if (!errorList.includes(errorStr)) errorList.push(errorStr);
        }
      }

      if (currentErrors.provenance && currentErrors.provenance.length > 0) {
        for (const error of currentErrors.provenance) {
          const errorStr = `Provenance [${error.field}]: ${[...new Set(error.errors)].join(", ")}`;
          if (!errorList.includes(errorStr)) errorList.push(errorStr);
        }
      }

      if (currentErrors.crossRef && currentErrors.crossRef.length > 0) {
        for (const error of currentErrors.crossRef) {
          const errorStr = `Cross reference [${error.field}]: ${[...new Set(error.errors)].join(", ")}`;
          if (!errorList.includes(errorStr)) errorList.push(errorStr);
        }
      }

      if (currentErrors.classification && currentErrors.classification.length > 0) {
        for (const error of currentErrors.classification) {
          const errorStr = `Classification [${error.field}]: ${[...new Set(error.errors)].join(", ")}`;
          if (!errorList.includes(errorStr)) errorList.push(errorStr);
        }
      }

      if (currentErrors.organisation && currentErrors.organisation.length > 0) {
        for (const error of currentErrors.organisation) {
          const errorStr = `Organisation [${error.field}]: ${[...new Set(error.errors)].join(", ")}`;
          if (!errorList.includes(errorStr)) errorList.push(errorStr);
        }
      }

      if (currentErrors.successorCrossRef && currentErrors.successorCrossRef.length > 0) {
        for (const error of currentErrors.successorCrossRef) {
          const errorStr = `Successor cross reference [${error.field}]: ${[...new Set(error.errors)].join(", ")}`;
          if (!errorList.includes(errorStr)) errorList.push(errorStr);
        }
      }

      if (currentErrors.note && currentErrors.note.length > 0) {
        for (const error of currentErrors.note) {
          const errorStr = `Note [${error.field}]: ${[...new Set(error.errors)].join(", ")}`;
          if (!errorList.includes(errorStr)) errorList.push(errorStr);
        }
      }

      return errorList.join("\n");
    };

    if (
      propertyContext.currentPropertyHasErrors &&
      propertyContext.currentErrors &&
      propertyContext.currentErrors.pkId
    ) {
      if (!failedIds.current.includes(propertyContext.currentErrors.pkId)) {
        failedIds.current = [...failedIds.current, propertyContext.currentErrors.pkId];

        const failedAddress = properties.current
          ? properties.current.find((x) => x.id === propertyContext.currentErrors.pkId)
          : null;

        if (failedAddress) {
          if (updateErrors.current)
            updateErrors.current = [
              ...updateErrors.current,
              {
                id: propertyContext.currentErrors.pkId,
                uprn: failedAddress.uprn,
                address: failedAddress.address,
                errors: getErrorList(propertyContext.currentErrors),
              },
            ];
          else
            updateErrors.current = [
              {
                id: propertyContext.currentErrors.pkId,
                uprn: failedAddress.uprn,
                address: failedAddress.address,
                errors: getErrorList(propertyContext.currentErrors),
              },
            ];
          if (Array.isArray(updateErrors.current)) setFinaliseErrors(updateErrors.current);
          else setFinaliseErrors([updateErrors.current]);
        }

        failedCount.current++;
        setRangeProcessedCount(updatedCount.current + failedCount.current);
      }
    }
  }, [propertyContext.currentErrors, propertyContext.currentPropertyHasErrors]);

  useEffect(() => {
    if (viewErrors && updateErrors.current && updateErrors.current.length > 0) {
      const errorIds = updateErrors.current.map((x) => x.uprn.toString());

      // if (errorIds && data && errorIds.length > 0 && errorIds.length !== data.length) {
      if (errorIds && data && errorIds.length > 0) {
        const errorData = data.filter((x) => errorIds.includes(x.uprn));

        setData(errorData);
        setAlertOpen(true);
        setViewErrors(false);
      }
    }
  }, [viewErrors, data, settingsContext.isScottish, settingsContext.isWelsh]);

  useEffect(() => {
    if (totalUpdateCount.current > 0 && totalUpdateCount.current === updatedCount.current + failedCount.current) {
      setHaveErrors(failedCount.current > 0);
      setCompleted(true);
      if (failedCount.current > 0) {
        setViewErrors(true);
      } else {
        setViewErrors(false);
        const savedMapProperties = savedProperty.current.map((x) => {
          return {
            uprn: x.uprn.toString(),
            parentUprn: x.parentUprn ? x.parentUprn.toString() : "",
            address: x.lpis.filter((rec) => rec.language === "ENG")[0].address.replaceAll("\r\n", " "),
            formattedAddress: x.lpis.filter((rec) => rec.language === "ENG")[0].address,
            postcode: x.lpis.filter((rec) => rec.language === "ENG")[0].postcode,
            easting: x.xcoordinate,
            northing: x.ycoordinate,
            logicalStatus: x.logicalStatus,
            classificationCode: getClassificationCode(x),
          };
        });
        const updatedSearchProperties = mapContext.currentMapSearchData.properties.map(
          (x) => savedMapProperties.find((rec) => Number(rec.uprn) === Number(x.uprn)) || x
        );
        mapContext.onSearchDataChange(
          mapContext.currentMapSearchData.streets,
          mapContext.currentMapSearchData.llpgStreets,
          updatedSearchProperties,
          null,
          null
        );
        if (onClose) onClose(true);
        else setShowDialog(false);
      }
      setUpdating(false);

      totalUpdateCount.current = 0;
      updatedCount.current = 0;
      failedCount.current = 0;
    }
  }, [rangeProcessedCount, mapContext, onClose]);

  useEffect(() => {
    if (selectionContext.closeControl) {
      selectionContext.onControlClosed();
      setChecked([]);
      checkedRef.current = [];
      mapContext.onHighlightClear();
    }
  }, [selectionContext, mapContext]);

  return (
    <Fragment>
      <Dialog
        open={showDialog}
        aria-labelledby="add-property-wizard-dialog"
        fullScreen
        onClose={handleDialogClose}
        slots={{ transition: Transition }}
      >
        <DialogTitle id="add-property-wizard-dialog" sx={{ ...dialogTitleStyle, mb: "0px" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-start"
              spacing={1}
              divider={<Divider orientation="vertical" flexItem />}
            >
              <Tooltip title="Exit editing seed points" sx={tooltipStyle}>
                <IconButton aria-label="close" onClick={handleCancelClick}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
              <Typography
                sx={{
                  flexGrow: 1,
                  display: "none",
                  pl: "8px",
                  pr: "8px",
                  [theme.breakpoints.up("sm")]: {
                    display: "block",
                  },
                }}
                variant="subtitle1"
                noWrap
                align="left"
              >
                {`Move BLPU seed point${propertyUprns && propertyUprns.length === 1 ? "" : "s"}`}
              </Typography>
            </Stack>
            <Button
              onClick={handleFinish}
              autoFocus
              disabled={updating}
              variant="contained"
              sx={haveErrors ? redButtonStyle : blueButtonStyle}
              startIcon={haveErrors ? <ErrorIcon /> : completed ? <CloseIcon /> : <ArrowForwardIcon />}
            >
              <Typography sx={{ fontSize: "20px", textTransform: "none" }}>{`${
                completed && !haveErrors ? "Close" : "Finish & Save"
              }`}</Typography>
            </Button>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: adsOffWhite, padding: "1px" }}>
          <Grid container spacing={0} justifyContent="flex-start">
            <Grid size={4}>
              <ADSWizardAddressList
                data={data}
                checked={checked}
                errors={finaliseErrors}
                haveMoveBlpu
                updating={updating}
                onCheckedChanged={handleCheckedChanged}
                onDataChanged={handleDataChanged}
                onErrorChanged={handleErrorChanged}
              />
            </Grid>
            <Grid size={8}>
              <ADSMapDialog startExtent={startExtent.current} data={data} isRange moveBlpu />
            </Grid>
          </Grid>
          {updating && <ADSCircularProgress display={updating} />}
        </DialogContent>
        <DialogActions
          sx={{
            borderTopWidth: "4px",
            borderTopStyle: "solid",
            borderTopColor: adsBlueA,
            justifyContent: "flex-start",
          }}
        >
          <Box sx={{ width: "100%", height: "40px", ml: theme.spacing(2), mr: theme.spacing(2), mt: theme.spacing(1) }}>
            <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1}>
              <Button
                onClick={handleFinish}
                autoFocus
                disabled={updating}
                variant="contained"
                sx={haveErrors ? redButtonStyle : blueButtonStyle}
                startIcon={haveErrors ? <ErrorIcon /> : completed ? <CloseIcon /> : <ArrowForwardIcon />}
              >
                <Typography sx={{ fontSize: "20px", textTransform: "none" }}>{`${
                  completed && !haveErrors ? "Close" : "Finish & Save"
                }`}</Typography>
              </Button>
            </Stack>
          </Box>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={alertOpen}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        onClose={handleAlertClose}
      >
        <Alert
          sx={GetAlertStyle(failedCount.current === 0)}
          icon={GetAlertIcon(failedCount.current === 0)}
          onClose={handleAlertClose}
          severity={GetAlertSeverity(failedCount.current === 0)}
          elevation={6}
          variant="filled"
        >
          "The properties left have failed validation."
        </Alert>
      </Snackbar>
      <MessageDialog isOpen={openMessageDialog} variant="cancelMoveBlpu" onClose={handleMessageDialogClose} />
    </Fragment>
  );
}

export default MoveBLPUDialog;
